import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { Colors, Icon, Text } from '@mercator-gcp/grout';
import { EmptyField } from 'components/elements/EmptyField';
import Tooltip from 'components/elements/Tooltip';
import { formatPrice } from 'utils/currency';
import { isNumber } from 'utils/validate';
import type { ProjectSize as TProjectSize } from 'types';

type TProps = {
  projectCost?: number | string | null;
  reportedMaxSize?: TProjectSize['reported_sqft_max'];
  estimatedMaxSize?: TProjectSize['estimated_sqft_max'];
};

const PricePerSqft = ({ projectCost, reportedMaxSize, estimatedMaxSize }: TProps) => {
  const pricePerSqft = useMemo(() => {
    // Get the sqft value - prefer reported over estimated, use max value
    let sqft: number | undefined;

    if (isNumber(reportedMaxSize) && reportedMaxSize !== 0) {
      sqft = reportedMaxSize;
    } else if (isNumber(estimatedMaxSize) && estimatedMaxSize !== 0) {
      sqft = estimatedMaxSize;
    }

    // Check if we have both project cost and sqft
    if (!projectCost || !sqft) {
      return null;
    }

    const cost = +projectCost;
    if (!isNumber(cost) || cost <= 0) {
      return null;
    }

    // Calculate price per sqft
    const pricePerSqftValue = cost / sqft;

    // Show empty state if price per sqft is less than $2
    if (pricePerSqftValue < 2) {
      return null;
    }

    return pricePerSqftValue;
  }, [projectCost, reportedMaxSize, estimatedMaxSize]);

  if (pricePerSqft === null) {
    return <EmptyField />;
  }

  return (
    <Box display="inline-flex" alignItems="center" whiteSpace="nowrap">
      <Text>{formatPrice(pricePerSqft)}/sqft&nbsp;</Text>
      <Tooltip tooltipText="Based on Total Project Value and Project Sqft">
        <Icon fill={Colors.TextQuiet} name={Icon.Name.InfoQuestionMark} />
      </Tooltip>
    </Box>
  );
};

export default PricePerSqft;
